import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonTitle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import type { RefresherCustomEvent } from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { GET_SEASON_COMPETITION_TEAMS } from '../../../graphql/queries/getSeasonCompetitionTeams';
import type {
  GetSeasonCompetitionTeamsQuery,
  GetSeasonCompetitionTeamsQueryVariables,
} from '../../../types/graphql-generated';
import { MatchList } from './components/MatchList';
import { MatchWeekStandings } from './components/MatchWeekStandings';
import { MatchWeekSelector } from './components/MatchWeekSelector';
import { useMatchWeekSelection } from './hooks/useMatchWeekSelection';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

type Segment = 'matches' | 'standings';

const CompetitionTab: React.FC = () => {
  const [selectedSegment, setSelectedSegment] = useState<Segment>('matches');
  const matchWeekSelection = useMatchWeekSelection();
  const selectedOrDefaultSeasonCompetitionId = matchWeekSelection.selectedSeasonCompetitionId;
  const { data, loading, error, refetch } = matchWeekSelection.matchesQuery;

  const { data: teamsData } = useQuery<GetSeasonCompetitionTeamsQuery, GetSeasonCompetitionTeamsQueryVariables>(
    GET_SEASON_COMPETITION_TEAMS,
    {
      variables: { seasonCompetitionId: selectedOrDefaultSeasonCompetitionId },
    }
  );

  const seasonCompetitionTeams = teamsData?.getSeasonCompetitionTeams?.teams ?? [];

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      await refetch();
    } finally {
      ev.detail.complete();
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonTitle className="ml-4">Competición</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <MatchWeekSelector selection={matchWeekSelection} />

        {/* Segment selector */}
        <IonSegment
          value={selectedSegment}
          onIonChange={(e) => setSelectedSegment(e.detail.value as Segment)}
          style={{ padding: '8px 16px' }}
        >
          <IonSegmentButton value="matches">
            <IonLabel>Partidos</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="standings">
            <IonLabel>Clasificación</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {/* Content */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <IonSpinner />
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', textAlign: 'center', color: 'red' }}>
            Error al cargar los datos: {error.message}
          </div>
        )}

        {!loading && !error && data && !data.getMatches && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
            No hay jornadas registradas por ahora.
          </div>
        )}

        {data?.getMatches && (
          <>
            {selectedSegment === 'matches' && <MatchList matches={data.getMatches.matches} />}
            {selectedSegment === 'standings' && (
              <MatchWeekStandings
                standings={data.getMatches.standings}
                seasonCompetitionTeams={seasonCompetitionTeams}
                currentWeekNumber={data.getMatches.currentMatchWeek.weekNumber}
              />
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default CompetitionTab;
